import React, { useState, useEffect, useCallback } from 'react';
import { normalizeClientCabinetEmailDisplay } from '../../utils/normalizeClientCabinetEmailDisplay';
import { useBackdropClose } from '../../hooks/useBackdropClose';
import './client-create-panel.css';

/**
 * ClientCreatePanel — панель создания / редактирования клиента
 * По макету Figma (SVG node 604_88553)
 *
 * Заменяет Ant Design Modal. Overlay + правая панель 480 px.
 */

// ==================== SVG ICONS ====================

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ==================== MAIN COMPONENT ====================

const ClientCreatePanel = ({
  visible = false,
  onClose = null,
  onSubmit = null,
  submitting = false,
  editingClient = null,
}) => {
  // ==================== FORM STATE ====================
  const [login, setLogin] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});

  // ==================== RESET / POPULATE ON OPEN ====================
  useEffect(() => {
    if (visible) {
      if (editingClient) {
        setLogin(editingClient.login || '');
        setEmail(normalizeClientCabinetEmailDisplay(editingClient.email) || '');
        setPassword('');
      } else {
        setLogin('');
        setEmail('');
        setPassword('');
      }
      setErrors({});
    }
  }, [visible, editingClient]);

  // ==================== VALIDATION ====================
  const validate = useCallback(() => {
    const newErrors = {};

    if (!login.trim()) {
      newErrors.login = 'Введите логин клиента';
    } else if (login.trim().length < 3) {
      newErrors.login = 'Логин должен содержать минимум 3 символа';
    }

    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      newErrors.email = 'Введите корректный адрес электронной почты';
    }

    if (password.trim() && password.trim().length < 6) {
      newErrors.password = 'Пароль должен содержать минимум 6 символов';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [login, email, password]);

  // ==================== SUBMIT ====================
  const handleSubmit = useCallback(() => {
    if (!validate()) return;
    if (!onSubmit) return;

    const values = {
      login: login.trim(),
      email: email.trim() || undefined,
      password: password.trim() || undefined,
    };

    onSubmit(values);
  }, [login, email, password, validate, onSubmit]);

  // ==================== CLOSE ====================
  const handleClose = useCallback(() => {
    if (!submitting && onClose) onClose();
  }, [submitting, onClose]);

  // ==================== KEYBOARD ====================
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  // Затемнение закрывает форму только настоящим щелчком — нажатие и отпускание на нём (просьба владельца 28.10)
  const backdrop = useBackdropClose(handleClose);

  // ==================== EARLY RETURN ====================
  if (!visible) return null;

  const isEditing = !!editingClient;

  return (
    <div
      className="ccp-overlay"
      {...backdrop}
    >
      <div className="ccp" role="dialog" aria-label={isEditing ? 'Редактирование клиента' : 'Создание клиента'}>

        {/* ==================== HEADER ==================== */}
        <div className="ccp__header">
          <h2 className="ccp__title">
            {isEditing ? 'редактирование клиента' : 'создание клиента'}
          </h2>
          <button
            type="button"
            className="ccp__close-btn"
            onClick={handleClose}
            aria-label="Закрыть"
            tabIndex={0}
          >
            <CloseIcon />
          </button>
        </div>

        {/* ==================== BODY ==================== */}
        <div className="ccp__body">
          <div className="ccp__section">

            {/* ---- Логин ---- */}
            <div className="ccp__field">
              <div className="ccp__label">
                <span className="ccp__label-required">*</span>
                <span className="ccp__label-text">Логин</span>
              </div>
              <input
                type="text"
                className={`ccp__text-input${errors.login ? ' ccp__text-input--error' : ''}`}
                placeholder="Введите логин клиента"
                value={login}
                onChange={(e) => {
                  setLogin(e.target.value);
                  if (errors.login) setErrors((prev) => ({ ...prev, login: undefined }));
                }}
                onKeyDown={handleKeyDown}
                aria-label="Логин клиента"
                aria-required="true"
                autoComplete="off"
                autoFocus
              />
              {errors.login && <span className="ccp__field-error">{errors.login}</span>}
            </div>

            {/* ---- Электронная почта ---- */}
            <div className="ccp__field">
              <div className="ccp__label">
                <span className="ccp__label-text">Электронная почта</span>
              </div>
              <input
                type="email"
                className={`ccp__text-input${errors.email ? ' ccp__text-input--error' : ''}`}
                placeholder="Введите адрес электронной почты"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
                }}
                onKeyDown={handleKeyDown}
                aria-label="Электронная почта"
                autoComplete="off"
              />
              {errors.email && <span className="ccp__field-error">{errors.email}</span>}
            </div>

            {/* ---- Пароль ---- */}
            <div className="ccp__field">
              <div className="ccp__label">
                <span className="ccp__label-text">Пароль</span>
              </div>
              <input
                type="text"
                className={`ccp__text-input${errors.password ? ' ccp__text-input--error' : ''}`}
                placeholder="Введите пароль клиента"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors((prev) => ({ ...prev, password: undefined }));
                }}
                onKeyDown={handleKeyDown}
                aria-label="Пароль клиента"
                autoComplete="new-password"
              />
              {errors.password && <span className="ccp__field-error">{errors.password}</span>}
            </div>

            {/* ---- Info box ---- */}
            <div className="ccp__info-box">
              <p className="ccp__info-box-text">
                {isEditing
                  ? 'Оставьте пароль пустым, чтобы не изменять текущий'
                  : 'Если пароль не указан, то будет сгенерирован автоматически'}
              </p>
            </div>

          </div>
        </div>

        {/* ==================== FOOTER ==================== */}
        <div className="ccp__footer">
          <button
            type="button"
            className="ccp__btn ccp__btn--cancel"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Отмена"
            tabIndex={0}
          >
            Отмена
          </button>
          <button
            type="button"
            className="ccp__btn ccp__btn--submit"
            onClick={handleSubmit}
            disabled={submitting}
            aria-label={isEditing ? 'Сохранить' : 'Создать'}
            tabIndex={0}
          >
            {submitting
              ? (isEditing ? 'Сохранение...' : 'Создание...')
              : (isEditing ? 'Сохранить' : 'Создать')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientCreatePanel;
